import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { useActivateCoupon, useCoupons, useEndCoupon, usePauseCoupon, usePromotions } from '../../hooks';
import { formatNumber } from '../../lib/format';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import type { ToastSeverity } from '../../lib/useToast';
import { COUPON_STATUS_COLOR } from '../../model/statuses';
import type { CouponResponse } from '../../model/types';
import { CouponFormDrawer } from './CouponFormDrawer';

export interface CouponsListPanelProps {
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** "Coupons" — coupon list (plan-011 §8.5). No server-side search — filters client-side over a
 *  generously-sized page (the BFF's `listCoupons` only supports page/size). */
export function CouponsListPanel({ onResult }: CouponsListPanelProps) {
  const { t } = useTranslation('promotions');
  const navigate = useNavigate();
  const { canManage } = usePromoPermissions();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuTarget, setMenuTarget] = useState<{ anchor: HTMLElement; coupon: CouponResponse } | null>(null);

  const query = useCoupons({ size: 200 });
  const promotions = usePromotions({ size: 200 });
  const activateMutation = useActivateCoupon();
  const pauseMutation = usePauseCoupon();
  const endMutation = useEndCoupon();

  const promotionName = (promotionId: string) => promotions.data?.content.find((p) => p.id === promotionId)?.name;

  const filtered = useMemo(() => {
    const all = query.data?.content ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => (c.name ?? '').toLowerCase().includes(q));
  }, [query.data, search]);

  const pageItems = filtered.slice(page * size, page * size + size);

  if (query.isLoading) return <LoadingState label={t('couponsList.loading')} />;
  if (query.isError) {
    return (
      <ErrorState
        title={t('couponsList.loadError.title')}
        message={query.error?.message ?? t('common:error.unknown')}
        onRetry={() => void query.refetch()}
      />
    );
  }

  if ((query.data?.content.length ?? 0) === 0) {
    return (
      <EmptyState
        icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 48 }} />}
        title={t('couponsList.empty.title')}
        description={t('couponsList.empty.description')}
        action={
          canManage ? (
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDrawerOpen(true)}>
              {t('couponsList.newCoupon')}
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <TextField
          size="small"
          placeholder={t('couponsList.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 260 }}
        />
        <Stack sx={{ flex: 1 }} />
        {canManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDrawerOpen(true)}>
            {t('couponsList.newCoupon')}
          </Button>
        ) : null}
      </Stack>

      <TablePageShell
        footer={
          <CompactTablePagination
            count={filtered.length}
            page={page}
            rowsPerPage={size}
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of')} ${count}`}
          />
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('couponsList.table.name')}</TableCell>
                <TableCell>{t('couponsList.table.promotion')}</TableCell>
                <TableCell>{t('couponsList.table.kind')}</TableCell>
                <TableCell align="right">{t('couponsList.table.limits')}</TableCell>
                <TableCell>{t('couponsList.table.status')}</TableCell>
                <TableCell align="right">{t('promotionsList.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.map((c) => (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/promo/coupons/${c.id}`)}>
                  <TableCell>{c.name || t('builder.unnamedCoupon')}</TableCell>
                  <TableCell>{promotionName(c.promotionId) ?? '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={t(`couponFormDrawer.kind.${c.kind}`)} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {c.maxRedemptionsTotal != null ? formatNumber(c.maxRedemptionsTotal) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={COUPON_STATUS_COLOR[c.status]} label={c.status} />
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      aria-label={t('promotionsList.rowMenuAria')}
                      onClick={(e) => setMenuTarget({ anchor: e.currentTarget, coupon: c })}
                    >
                      <MoreVertRoundedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TablePageShell>

      <Menu anchorEl={menuTarget?.anchor} open={Boolean(menuTarget)} onClose={() => setMenuTarget(null)}>
        <MenuItem
          onClick={() => {
            if (menuTarget) navigate(`/promo/coupons/${menuTarget.coupon.id}`);
            setMenuTarget(null);
          }}
        >
          {t('common:action.view')}
        </MenuItem>
        {canManage && menuTarget?.coupon.status === 'DRAFT' ? (
          <MenuItem
            onClick={() => {
              if (!menuTarget) return;
              activateMutation.mutate(menuTarget.coupon.id, {
                onSuccess: () => onResult('success', t('couponsList.activated')),
                onError: (e) => onResult('error', e.message),
              });
              setMenuTarget(null);
            }}
          >
            {t('couponDetail.activate')}
          </MenuItem>
        ) : null}
        {canManage && menuTarget?.coupon.status === 'ACTIVE' ? (
          <MenuItem
            onClick={() => {
              if (!menuTarget) return;
              pauseMutation.mutate(menuTarget.coupon.id, {
                onSuccess: () => onResult('success', t('couponsList.deactivated')),
                onError: (e) => onResult('error', e.message),
              });
              setMenuTarget(null);
            }}
          >
            {t('couponDetail.deactivate')}
          </MenuItem>
        ) : null}
        {canManage && menuTarget && menuTarget.coupon.status !== 'ENDED' ? (
          <MenuItem
            onClick={() => {
              if (!menuTarget) return;
              endMutation.mutate(menuTarget.coupon.id, {
                onSuccess: () => onResult('success', t('couponDetail.ended')),
                onError: (e) => onResult('error', e.message),
              });
              setMenuTarget(null);
            }}
          >
            {t('couponDetail.end')}
          </MenuItem>
        ) : null}
      </Menu>

      <CouponFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => void query.refetch()}
        onResult={onResult}
      />
    </>
  );
}
